import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button,
  Paper,
  Stack,
  Typography } from '@mui/material';
import { VyIcon } from '@weft/icons';
import type { Meter, MeterInput, NewMeter } from '@weft/types';
import { MeterInputSchema } from '@weft/types';
import { AGGREGATION_OPERATORS, NewMeterSchema } from '@weft/types';
import { isNullOrUndefined, isObject } from '@weft/utils';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { Navigate, useLocation } from 'react-router-dom';
import VyDialog from '../../../components/new-design/feedback/VyDialog';
import { useCreateMeter } from '../../../hooks/requests/use-create-meter';
import { useUpdateMeter } from '../../../hooks/requests/use-update-meter';
import { useDialog } from '../../../hooks/use-dialog';
import { useVyNavigate } from '../../../hooks/use-vy-navigate';
import { runMutation } from '../../../utils/vy-mutation';
import { CreateProductPageAppBar } from './create-product-page-components/CreateProductPageAppBar';
import { CreateProductPageForm } from './create-product-page-components/CreateProductPageForm';

export const CreateProductPage: React.FC = () => {
  const navigate = useVyNavigate();
  const location = useLocation();
  const productName = location.state?.name;

  const { isDialogOpen: isUpdateMeterDialogOpen, handleOpen: handleOpenUpdateMeterDialog, handleClose: handleCloseUpdateMeterDialog } = useDialog();

  const queryClient = useQueryClient();

  const refreshProductPage = async () => {
    await queryClient.invalidateQueries('products-page');
  };

  const createMeterMutation = useCreateMeter({
    onSuccess: refreshProductPage,
  });
  const updateMeterMutation = useUpdateMeter({
    onSuccess: refreshProductPage,
  });

  const methods = useForm<MeterInput, unknown, NewMeter>({
    resolver: zodResolver(NewMeterSchema),
    defaultValues: isMeterInput(location.state) ? location.state : {
      name: productName,
      eventName: '',
      aggregationMethod: {
        operator: AGGREGATION_OPERATORS.Count,
      },
    },
  });

  const handleSubmit = methods.handleSubmit(async (newMeter) => {
    if (doesMeterHaveId(location.state)) {
      await runMutation(updateMeterMutation,
        {
          id: location.state.id,
          ...newMeter,
        },
        {
          pending: 'Updating product...',
          success: 'Product updated',
          error: 'Failed to update product',
        });
    } else {
      await runMutation(createMeterMutation, newMeter, {
        pending: 'Creating product...',
        success: 'Product created',
        error: 'Failed to create product',
      });
    }

    navigate('/products', {
      replace: true,
    });
  });

  if (isNullOrUndefined(productName)) {
    return (
      <Navigate to="/products"/>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: '#FAFAFA',
        overflowY: 'hidden',
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit}>
          <CreateProductPageAppBar />

          <Box sx={{
            height: 'calc(100vh - 133px)',
            overflowY: 'auto',
          }}
          >
            <Stack
              margin="auto"
              textAlign="center"
              padding="40px 56px"
              rowGap="24px"
              maxWidth="lg"
            >
              <Stack rowGap={2}>
                <Typography variant="h5">Create Product</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Start by selecting the event you want to track.
                  <br/>
                  This will help set the conditions and managing actions in your workflow.
                </Typography>
              </Stack>

              <Paper sx={theme => ({
                border: `1px solid ${theme.palette.divider}`,
                padding: '24px',
                borderRadius: '14px',
              })}
              >
                <CreateProductPageForm/>
              </Paper>
            </Stack>
          </Box>

          <Box sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            zIndex: 1000,
            justifyContent: 'end',
            textAlign: 'right',
            background: '#fff',
            height: '68px',
            padding: '16px',
            borderTop: '1px solid #E0E0E0',
          }}
          >
            <VyDialog
              open={isUpdateMeterDialogOpen}
              onClose={handleCloseUpdateMeterDialog}
              component="form"
              label={"You're about to update this product"}
              ctaText={(
                <>
                  Save Product
                  <VyIcon
                    icon="ArrowSmallRight"
                    style={{
                      marginRight: '-6px',
                    }}
                  />
                </>
              )}
              ctaButtonProps={{
                type: 'submit',
                disabled: methods.formState.isSubmitting,
                sx:{
                  fontWeight: 400,
                  columnGap: 1,
                },
              }}
            >
              <Typography>
                Are you sure you want to update this product?
              </Typography>
            </VyDialog>

            <Button
              variant="contained"
              color="primary"
              size="medium"
              sx={{
                fontWeight: 400,
                columnGap: 1,
              }}
              disabled={!methods.formState.isValid || methods.formState.isSubmitting}
              type={doesMeterHaveId(location.state) ? 'button' : 'submit'}
              onClick={doesMeterHaveId(location.state) ? handleOpenUpdateMeterDialog : undefined}
            >
              Save Product
              <VyIcon
                icon="ArrowSmallRight"
                style={{
                  marginRight: '-6px',
                }}
              />
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
};

function isMeterInput(meter: unknown): meter is MeterInput {
  return MeterInputSchema.safeParse(meter).success;
}

function doesMeterHaveId(meter: unknown): meter is { id: string } {
  return isObject(meter) && ('id' satisfies keyof Meter) in meter;
}